<template>
    <SettingsLayout>
        <SettingsSubHeader v-if="!hasNoTeamsNorInvitations">
            <template #title>
                {{ $t("teams.title") }}
            </template>
            <template #description>
                <span>{{ $t("teams.description") }}</span>
                <a
                    v-if="lang === 'cs'"
                    href="https://napoveda.zaptime.cz/prace-v-tymu.html#tymova-spoluprace"
                    target="_blank"
                    class="ml-1 font-medium text-z-primary-600 hover:text-z-primary-500 underline"
                >
                    {{ $t("teams.learn_more") }}
                </a>
            </template>
            <template #actions>
                <CreateTeam :can-create="canCreateTeams">
                    <AddButton
                        :disabled="!canCreateTeams"
                        data-cy="add-new-team"
                    >
                        {{ $t("teams.new_team") }}</AddButton
                    >
                </CreateTeam></template
            >
        </SettingsSubHeader>

        <div class="px-4 sm:px-6 pt-[1px]">
            <template v-for="invitation in invitations">
                <Invitation :invitation="invitation" />
            </template>

            <template v-if="Object.keys(teams).length > 0">
                <Team v-for="team in teams" :key="team.id" :team="team" />
            </template>

            <template v-if="hasNoTeamsNorInvitations">
                <EmptyStateWithIcon
                    class="mt-[51px]"
                    :title="$t('teams.empty_state.title')"
                >
                    <template #description>
                        <span>{{ $t("teams.empty_state.description") }}</span>
                        <a
                            v-if="lang === 'cs'"
                            href="https://napoveda.zaptime.cz/prace-v-tymu.html#tymova-spoluprace"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="ml-1 font-medium text-z-primary-600 hover:text-z-primary-500 underline"
                        >
                            {{ $t("teams.learn_more") }}
                        </a>
                    </template>
                    <template #icon>
                        <svg
                            width="76"
                            height="76"
                            viewBox="0 0 76 76"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <g clip-path="url(#clip0_2222_28357)">
                                <path
                                    d="M53.4235 59.2579L52.3147 54.0411C51.7265 51.274 50.0632 48.8538 47.6906 47.313C45.318 45.7722 42.4305 45.2371 39.6634 45.8253L24.013 49.1518C21.2459 49.74 18.8257 51.4034 17.2849 53.7759C15.7442 56.1485 15.209 59.036 15.7972 61.8031L16.906 67.0199M69.0739 55.9313L67.965 50.7145C67.4719 48.4031 66.2252 46.321 64.4205 44.795C62.6158 43.2689 60.3554 42.3855 57.9942 42.2833M43.5159 12.6459C45.8823 12.7435 48.149 13.6259 49.9585 15.154C51.768 16.6822 53.0175 18.7691 53.5099 21.0858C54.0023 23.4025 53.7097 25.8172 52.6782 27.9492C51.6466 30.0812 49.9349 31.8093 47.8127 32.861M37.8363 24.4037C39.0611 30.166 35.3828 35.8302 29.6205 37.055C23.8582 38.2798 18.194 34.6014 16.9692 28.8391C15.7444 23.0768 19.4227 17.4126 25.185 16.1878C30.9473 14.963 36.6115 18.6414 37.8363 24.4037Z"
                                    stroke="url(#paint0_linear_2222_28357)"
                                    stroke-width="4"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </g>
                            <defs>
                                <linearGradient
                                    id="paint0_linear_2222_28357"
                                    x1="13.5897"
                                    y1="23.5609"
                                    x2="57.9413"
                                    y2="58.411"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#FF8D47" />
                                    <stop
                                        offset="0.147634"
                                        stop-color="#FF7447"
                                    />
                                    <stop
                                        offset="0.454086"
                                        stop-color="#FF4247"
                                    />
                                    <stop
                                        offset="0.636926"
                                        stop-color="#E92A5B"
                                    />
                                    <stop
                                        offset="0.740367"
                                        stop-color="#DD1C67"
                                    />
                                    <stop
                                        offset="0.842991"
                                        stop-color="#D31071"
                                    />
                                    <stop offset="1" stop-color="#CF0283" />
                                </linearGradient>
                                <clipPath id="clip0_2222_28357">
                                    <rect
                                        width="64"
                                        height="64"
                                        fill="white"
                                        transform="translate(0.0461426 13.3525) rotate(-12)"
                                    />
                                </clipPath>
                            </defs>
                        </svg>
                    </template>
                </EmptyStateWithIcon>

                <div class="mt-8 flex justify-center">
                    <CreateTeam
                        :defalut-open="params.create === 'true'"
                        :can-create="canCreateTeams"
                    >
                        <AddButton
                            class="flex"
                            :paid-only="!canCreateTeams"
                            feature="teams"
                            data-cy="create-new-day-off"
                        >
                            {{ $t("teams.new_team") }}</AddButton
                        >
                    </CreateTeam>
                </div>
            </template>
        </div>
    </SettingsLayout>
</template>

<script lang="ts" setup>
import SettingsLayout from "@/Layouts/SettingsLayout.vue";
import SettingsSubHeader from "@/Layouts/components/SettingsSubHeader.vue";

import AddButton from "../Atomic/AddButton.vue";
import Team from "@/Pages/Zaptime/App/Teams/components/Team.vue";
import CreateTeam from "@/Pages/Zaptime/App/Teams/components/CreateTeam.vue";
import Invitation from "@/Pages/Zaptime/App/Teams/components/Invitation.vue";
import { EmptyStateWithIcon } from "@/components/empty-state-with-icon";
import { useUrlSearchParams } from "@vueuse/core";
import type { Team as TeamType, TeamInvitation } from "./types/Team";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
const params = useUrlSearchParams();

const lang = useI18n().locale.value;

interface Props {
    teams: TeamType[];
    invitations?: TeamInvitation[];
    canCreateTeams: boolean;
}

const props = defineProps<Props>();

const hasNoTeamsNorInvitations = computed(() => {
    return (
        props.teams.length === 0 &&
        props.invitations &&
        props.invitations.length === 0
    );
});
</script>
